// Track configuration — single source of truth for the eight programs.
// Copy is wellness-focused (9th-grade reading level). No disease claims, no
// "treat/cure/reverse/lower [disease metric]" language. Body composition,
// biological-age, and engine outputs are wellness estimates — not diagnostics.
export const TRACKS = [
  {
    slug: "body-transformation",
    name: "Body Transformation",
    short: "BT",
    code: "BT.01",
    tagline: "Look the way you want to look — for real this time.",
    subline: "Body transformation & body-composition coaching.",
    summary:
      "Lose fat. Build muscle. See the change. Our flagship program builds your body using a smart 4-part system — food, training, recovery, and a body scan every 2 weeks so you actually see progress.",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/2b9rpxdv_couple%202.png",
    accent: "#D90429",
    flagship: true,
    pillars: [
      { title: "Food That Fits You", body: "A simple eating plan built around your body, not a generic diet." },
      { title: "Training That Works", body: "Workouts matched to your level — no cookie-cutter routines." },
      { title: "Recovery Done Right", body: "Better sleep, less stress, faster results." },
      { title: "Body Scan Every 2 Weeks", body: "Smart camera scans show your progress. No more guessing." },
    ],
    outcomes: [
      "Lose fat and keep it off",
      "Build real, visible muscle",
      "Get stronger every month",
      "See your progress on a real chart",
    ],
    jotformId: "PLACEHOLDER_BT_FORM_ID",
    deepDiveLabel: "Body Intake",
    patentTech: {
      code: "05",
      name: "Body Composition Engine",
      blurb: "A wellness estimate of body composition trends from your phone — for progress tracking, not a medical test.",
    },
  },
  {
    slug: "metabolic-optimization",
    name: "Metabolic Optimization",
    short: "MO",
    code: "MO.02",
    tagline: "Steady energy, fewer crashes, and a metabolism that works with you.",
    subline: "Metabolic health & energy optimization.",
    summary:
      "Dial in the engine — energy, body composition, and metabolic fitness. We use your wearable data to tune food, movement, and recovery so your days run steadier and your training compounds.",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/kqw6des0_cardiohelp.png",
    accent: "#2563EB",
    flagship: false,
    pillars: [
      { title: "Energy & Recovery", body: "We use your wearable signals to find when your energy dips — and what to change." },
      { title: "Food Response", body: "A short tracking window shows how your body responds to meals." },
      { title: "Your Plan", body: "Food, movement, and recovery — built around how your body actually runs." },
      { title: "Re-tune Quarterly", body: "Every 90 days we revisit your data and adjust the plan." },
    ],
    outcomes: [
      "Steadier energy day to day",
      "Fewer crashes after meals",
      "Body composition trends in the right direction",
      "A plan you can see working in the data",
    ],
    jotformId: "PLACEHOLDER_MO_FORM_ID",
    deepDiveLabel: "Metabolic Intake",
  },
  {
    slug: "digestive-microbiome",
    name: "Digestive & Microbiome Optimization",
    short: "GI",
    code: "GI.03",
    tagline: "A calmer gut. Steadier everything.",
    subline: "Gut health & microbiome wellness program.",
    summary:
      "A coached program that optimizes the daily inputs to digestion and gut comfort — food, fiber, routine, and habits — with an AI-built plan that adapts as your comfort and regularity trend improve.",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/3dpt6i8c_GI%20Tract.png",
    accent: "#2563EB",
    flagship: false,
    manifesto: {
      headline: "Start where health starts.",
      body: "Gut comfort drives energy, focus, and how the rest of your plan lands — so we build it deliberately, not as an afterthought.",
    },
    scanReadouts: [
      { label: "Comfort Score", value: "8.3" },
      { label: "Regularity", value: "↑" },
      { label: "Bloating", value: "↓" },
    ],
    pillars: [
      { title: "Assess", body: "Digestion, comfort, food response, and routine — all in one check-in." },
      { title: "Track", body: "Weekly comfort + regularity trend, with an optional food log." },
      { title: "Plan", body: "AI nutrition, fiber, and habit plan — coach-reviewed." },
      { title: "Adapt", body: "Adjust as your gut comfort and regularity trend moves." },
    ],
    outcomes: [
      "A daily gut comfort score that trends up",
      "Steadier regularity, less bloating",
      "More energy, fewer crashes",
      "Food habits that fit how your body responds",
    ],
    jotformId: "PLACEHOLDER_GI_FORM_ID",
    deepDiveLabel: "Digestive Intake",
    techCards: [
      { title: "Body composition from your phone", note: "Patent pending. Wellness estimate for progress tracking — not diagnostic." },
      { title: "Programming built for you", note: "Custom AI, coach-reviewed." },
      { title: "Velocity & plateau detection", note: "Longitudinal tracking." },
      { title: "Gut comfort trend tracking", note: "The daily signals behind digestion and energy, tracked as a trend you can actually move." },
    ],
    whoForCards: [
      { title: "The uncomfortable", body: "Bloating, irregularity, food that doesn't sit right." },
      { title: "The reset-seeker", body: "Wants to rebuild gut habits from the ground up." },
      { title: "The whole-body optimizer", body: "Knows the gut drives energy, mood, and immunity." },
    ],
    faq: [
      {
        q: "Is this medical treatment for a digestive condition?",
        a: "No. Digestive & Microbiome Optimization is an optimization program focused on digestion, comfort, food habits, and routine for general well-being. It does not diagnose or treat any condition; if clinical support is needed, we refer you to the affiliated medical team.",
      },
      {
        q: "Do I need a stool test or lab work?",
        a: "Optional. You can layer deeper testing in through the medical team if you want more data.",
      },
      {
        q: "What do I do each week?",
        a: "A short check-in: a few comfort and regularity questions, your habits, and how the week went. The system and your coach handle the rest.",
      },
      {
        q: "What if certain foods bother me?",
        a: "We map tolerance gradually and adjust your plan — no aggressive, long-term elimination.",
      },
    ],
    referralNote: "Clinical concerns are referred to the affiliated medical team.",
    finalCta: {
      headline: "See if Digestive & Microbiome Optimization is right for you.",
      primary: { label: "Apply now", href: "#unified-model" },
      secondary: { label: "Talk to the team", href: "mailto:hello@integratedwellness.com" },
    },
  },
  {
    slug: "longevity-optimization",
    name: "Longevity Optimization",
    short: "LO",
    code: "LO.04",
    tagline: "Add healthy years. Not just calendar years.",
    subline: "Longevity & healthy-aging program.",
    summary:
      "Your real age isn't the number on your birthday cake. It's how your body is actually trending. We measure that — and build a plan to keep it moving the right direction.",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/sjl0fnq7_OMPc5jDlAFkCPhjjkCEjg.png",
    accent: "#2563EB",
    flagship: false,
    pillars: [
      { title: "Your Real Age", body: "We estimate how old your body is trending — for tracking your progress." },
      { title: "How You're Aging", body: "We watch the wellness markers that move with age — and help you shift them." },
      { title: "Your Plan", body: "A simple plan to add good years to your life." },
      { title: "Expert Review", body: "Top experts review every step." },
    ],
    outcomes: [
      "A real-age estimate that trends in your favor",
      "More energy now",
      "Wellness markers moving in the right direction",
      "A long-term plan that actually works",
    ],
    jotformId: "PLACEHOLDER_LO_FORM_ID",
    deepDiveLabel: "Longevity Intake",
    patentTech: {
      code: "06",
      name: "Integrated Biological Age (IBA) 2.0",
      blurb: "An estimate of how your body is aging, from your data — for tracking trends, not a medical test.",
    },
  },
  {
    slug: "performance-optimization",
    name: "Performance Optimization",
    short: "PO",
    code: "PO.05",
    tagline: "Train harder. Recover faster. Push your ceiling.",
    subline: "Athletic performance & recovery coaching.",
    summary:
      "Built for people who want more — more strength, more stamina, more bounce-back. Train smarter using real data from your body.",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/drgbileo_MRn0ppqRyNjWUKsGV8FeC.png",
    accent: "#D90429",
    flagship: false,
    pillars: [
      { title: "Fitness Test", body: "We see exactly where you are — so we know where to push." },
      { title: "Smart Training", body: "Workouts built for your body. Not someone else's." },
      { title: "Better Recovery", body: "Sleep, heart rate, and stress dialed in so you bounce back fast." },
      { title: "Re-test Every Quarter", body: "Numbers go up. You feel it." },
    ],
    outcomes: [
      "Stronger every month",
      "More stamina",
      "Faster recovery",
      "A higher ceiling — proven in the numbers",
    ],
    jotformId: "PLACEHOLDER_PO_FORM_ID",
    deepDiveLabel: "Performance Intake",
  },
  {
    slug: "cognitive-optimization",
    name: "Cognitive Optimization",
    short: "CO",
    code: "CO.06",
    tagline: "Sharper focus. Faster recall. Clearer days.",
    subline: "Focus, energy & cognitive optimization.",
    summary:
      "Your brain is a system you can train. We assess where it's at, find what's slowing you down, and dial it in.",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/362n26hq_miVozoJGZvyHk_e9fYVEs.png",
    accent: "#D90429",
    flagship: false,
    pillars: [
      { title: "Brain Check", body: "A simple wellness check shows your focus, recall, and speed today." },
      { title: "What's Holding You Back", body: "We find the gut, sleep, or nutrition habit pulling your focus down." },
      { title: "Your Plan", body: "Simple changes — food, sleep, training — built around your brain." },
      { title: "Re-check", body: "Numbers move. Focus comes back." },
    ],
    outcomes: [
      "Longer focus blocks",
      "Faster recall",
      "Sharper thinking",
      "Steadier energy through the day",
    ],
    jotformId: "PLACEHOLDER_CO_FORM_ID",
    deepDiveLabel: "Cognitive Intake",
  },
  {
    slug: "hormonal-vitality",
    name: "Hormonal Wellness & Vitality Optimization",
    short: "HV",
    code: "HV.07",
    tagline: "More energy. Better sleep. Real vitality.",
    subline: "Hormonal vitality & energy optimization.",
    summary:
      "A coached program that supports your body's natural hormonal balance through the levers that actually move it — sleep, training, stress, and nutrition — with an AI-built plan that adapts as your energy and recovery trend improve.",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/3y5osj7f_oAagLPi7eET9IAI2QV1OX.png",
    accent: "#D90429",
    flagship: false,
    manifesto: {
      headline: "Support your body's own balance.",
      body: "The biggest levers on natural vitality aren't pills — they're sleep, strength, stress, and how you fuel. We build those deliberately.",
    },
    scanReadouts: [
      { label: "Energy", value: "8.4" },
      { label: "Sleep Quality", value: "86%" },
      { label: "Recovery", value: "↑" },
    ],
    pillars: [
      { title: "Assess", body: "Sleep, stress, training, nutrition, energy and recovery — all in one check-in." },
      { title: "Track", body: "Weekly vitality + recovery trend, with an optional wearable overlay." },
      { title: "Plan", body: "AI sleep, training, stress and nutrition plan — coach-reviewed." },
      { title: "Adapt", body: "Adjust as your energy and recovery trend moves." },
    ],
    outcomes: [
      "Steadier energy through the day",
      "Better sleep, easier recovery",
      "Vitality trends moving the right direction",
      "Lifestyle habits that fit how your body actually responds",
    ],
    jotformId: "PLACEHOLDER_HV_FORM_ID",
    deepDiveLabel: "Vitality Intake",
    techCards: [
      { title: "Body composition from your phone", note: "Patent pending. Wellness estimate for progress tracking — not diagnostic." },
      { title: "Programming built for you", note: "Custom AI, coach-reviewed." },
      { title: "Velocity & plateau detection", note: "Longitudinal tracking." },
      { title: "Vitality & recovery trend tracking", note: "Sleep, stress, and recovery signals that drive natural energy, tracked as a trend you can move." },
    ],
    whoForCards: [
      { title: "The depleted", body: "Low energy, poor sleep, running on empty." },
      { title: "The recomposition-stuck", body: "Training hard, body and energy not responding." },
      { title: "The midlife optimizer", body: "Wants vitality and steady energy through lifestyle (cycle/perimenopause comfort support included)." },
    ],
    medicalPartner: {
      headline: "Want testing or hormone therapy? That's handled by our licensed medical hormone program.",
      body: "Hormonal Wellness & Vitality is a lifestyle program. For hormone testing, TRT, bioidentical HRT, or thyroid care, we connect you to our licensed Integrated Medical team.",
      cta: {
        label: "Learn about Medical Hormone Care",
        href: "https://integratednycai.com/medical-care",
      },
    },
    selfRouteCard: {
      title: "Looking for hormone testing, TRT, HRT or thyroid care?",
      body: "Those services live with our licensed medical hormone program — not this wellness intake. Start there instead and a coordinator will guide you.",
      cta: {
        label: "Go to Medical Hormone Care",
        href: "https://integratednycai.com/medical-care",
      },
    },
    faq: [
      {
        q: "Is this hormone therapy or testosterone/HRT treatment?",
        a: "No. Hormonal Wellness & Vitality is a lifestyle program that supports your body's natural balance through sleep, training, stress, and nutrition. It does not test, prescribe, or treat hormones. If you want testing or therapy, we connect you to our licensed medical hormone program.",
      },
      {
        q: "Will you check my hormone levels?",
        a: "Not on the wellness side. Hormone testing and interpretation are handled by the licensed medical team, which we're happy to introduce you to.",
      },
      {
        q: "Can this help with energy, sleep, and midlife changes?",
        a: "Yes — through lifestyle support for comfort, energy, and vitality. Anything requiring medical care is referred to our medical team.",
      },
      {
        q: "What do I do each week?",
        a: "A short check-in on energy, sleep, stress, training, and recovery. The system and your coach adjust your plan from there.",
      },
    ],
    referralNote: "Clinical concerns and hormone-level questions are referred to the affiliated medical team.",
    finalCta: {
      headline: "See if Hormonal Wellness & Vitality is right for you.",
      primary: { label: "Apply now", href: "#unified-model" },
      secondary: { label: "Talk to the team", href: "mailto:hello@integratedwellness.com" },
    },
  },
  {
    slug: "life-optimization",
    name: "Life Optimization",
    short: "LI",
    code: "LI.08",
    tagline: "Build a life that actually works for you.",
    subline: "Habit, behavior & life-optimization coaching.",
    summary:
      "Coaching across the parts of life that matter — habits, relationships, money, purpose. Built to work with your body programs, not separately.",
    image:
      "https://customer-assets.emergentagent.com/job_integrated-wellness-6/artifacts/0x4vaivy_life%20coaching.png",
    accent: "#2563EB",
    flagship: false,
    pillars: [
      { title: "Get Clear", body: "Figure out what you actually want — and why." },
      { title: "Build Habits That Stick", body: "Small, smart changes. Not willpower." },
      { title: "Relationships & Energy", body: "Fix what's draining you. Build what fills you up." },
      { title: "All Together", body: "Connects with your body programs — so nothing is wasted." },
    ],
    outcomes: [
      "Habits that actually stick for 90+ days",
      "Less stress, less drama",
      "A clearer head",
      "All your programs working together",
    ],
    jotformId: "PLACEHOLDER_LI_FORM_ID",
    deepDiveLabel: "Life Intake",
    patentTech: {
      code: "07",
      name: "Self-Regulation Console — BLS Engine",
      blurb: "A simple check-in tool that helps you stay calm, focused, and on track between sessions.",
    },
  },
];

export const TRACK_MAP = TRACKS.reduce((acc, t) => {
  acc[t.slug] = t;
  return acc;
}, {});

// Slugs that should display the digestive/gut cross-sell band on their pages.
export const GUT_CROSS_SELL_SLUGS = [
  "body-transformation",
  "metabolic-optimization",
  "longevity-optimization",
  "cognitive-optimization",
];

export const WELLNESS_WHEEL = [
  { slug: "physical", label: "Physical" },
  { slug: "nutritional", label: "Nutritional" },
  { slug: "cognitive", label: "Cognitive" },
  { slug: "emotional", label: "Emotional" },
  { slug: "social", label: "Social" },
  { slug: "purpose", label: "Purpose" },
  { slug: "financial", label: "Financial" },
  { slug: "spiritual", label: "Spiritual" },
];

export const FAQ_ITEMS = [
  {
    q: "What is a data-driven optimization program?",
    a: "It's medically supervised coaching that starts from your real data — your wearable, your intake, optional home tests — and builds a personalized optimization plan that adapts every week as your numbers move. It's the opposite of a static, one-size-fits-all program.",
  },
  {
    q: "How is this different from other optimization programs?",
    a: "Most apps hand you a generic plan and hope you stick with it. We start from your real data — your wearable, your intake, optional home tests — and build a plan that adapts every week as your numbers move. You also get one coordinator who knows you across every program, and a direct line to licensed medical care when you need it. It's coaching that actually changes as you do.",
  },
  {
    q: "What's the difference between Body and Metabolic optimization?",
    a: "Body Transformation is about how you look and move — losing fat, building muscle, and seeing the change with a body scan every two weeks. Metabolic Optimization is about how your body runs underneath that — your energy, cravings, and metabolic fitness through food, movement, and recovery. Many members run both; Body changes the outside, Metabolic steadies the engine.",
  },
  {
    q: "What's the difference between Longevity and Performance optimization?",
    a: "Performance Optimization is about output now — training harder, recovering faster, and pushing your ceiling. Longevity Optimization is about the long game — the habits and markers tied to how well you age, tracked over time. One sharpens today; the other protects the next few decades. They overlap, and plenty of members run both.",
  },
  {
    q: "Do I have to be an athlete to apply?",
    a: "Not at all. Most members aren't athletes — they're people who want more energy, a better body, sharper focus, or steadier health habits. Every program meets you where you are and scales to your level. Performance Optimization in particular works for anyone who wants to train smarter, not just competitors.",
  },
  {
    q: "How does signing up work?",
    a: "One short intake. You pick the program that fits, answer a few questions, and a real coordinator follows up within one business day to get you started. No long forms, no runaround.",
  },
  {
    q: "Can I run more than one optimization program at once?",
    a: "Yes — and they're built for it. Each program runs on the same core engine, so your data and plans stay in sync instead of working against each other. Your coordinator keeps everything coordinated, so running two or three feels like one connected plan, not three separate to-do lists.",
  },
  {
    q: "Does insurance cover this?",
    a: "Our optimization programs are wellness coaching and are typically self-pay, not billed to insurance. If your care moves into the medical side — labs, prescriptions, or supervised treatment through our licensed medical team — some of those services may be billable depending on your plan. Your coordinator can walk you through what's what.",
  },
];

export const STATS = [
  { value: "7", label: "Patents pending" },
  { value: "14d", label: "Body scan cadence" },
  { value: "7", label: "Programs" },
  { value: "1:1", label: "Program lead" },
];
